import { createAsyncThunk } from '@reduxjs/toolkit';

import loggerGenerator from '@travel/logger';

import { apiPath } from 'constants/api';
import { MEDIA_UPLOAD_TIMEOUT } from 'constants/media';

import type { ApiClient } from '@travel/api-client';
import type { Media } from 'store/apiTypes';
import { type MediaConfig, type MediaAPIResponse } from './type';
import { noticeError } from '@travel/tracker';

const logger = loggerGenerator();

export async function uploadNewMedia(
  file: File,
  apiClient: ApiClient,
  options?: {
    ignoreCommonErrorHandler?: boolean;
    abortController?: AbortController;
  },
): Promise<Media> {
  const formData = new FormData();

  formData.append('media', file, file.name);

  try {
    const data = await apiClient.post(apiPath.postMedia.pathname, {
      values: formData,
      timeout: MEDIA_UPLOAD_TIMEOUT,
      ignoreCommonErrorHandler: options?.ignoreCommonErrorHandler ?? true,
      abortController: options?.abortController,
    });

    return data as Media;
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      noticeError('Upload Media Failed', error);
    }

    throw error;
  }
}

export const deleteMedia = createAsyncThunk<
  MediaAPIResponse,
  { imageId: string; ignoreCommonErrorHandler?: boolean },
  { extra: { apiClient: ApiClient } }
>('media/delete', async (payload, { extra: { apiClient }, rejectWithValue }) => {
  try {
    const data = await apiClient.delete(apiPath.deleteMedia.pathname(payload.imageId), {
      ignoreCommonErrorHandler: payload.ignoreCommonErrorHandler ?? true,
    });

    return data;
  } catch (error) {
    noticeError('Delete Media Failed', error);

    return rejectWithValue(error);
  }
});

export const fetchMediaConfig = createAsyncThunk<
  MediaConfig,
  void,
  { extra: { apiClient: ApiClient } }
>('mediaConfig/fetch', async (_, { extra: { apiClient }, rejectWithValue }) => {
  try {
    if (!apiPath.mediaConfig.pathname) {
      throw new Error('Media Config Env Path Not Set');
    }

    const data = await apiClient.get(apiPath.mediaConfig.pathname, {
      ignoreCommonErrorHandler: true,
      isThirdPartyApi: true,
      customParams: {
        isStaticFile: true,
      },
    });

    return data;
  } catch (error) {
    logger.error('Fetch Media Config Failed', error);
    noticeError('Fetch Media Config Failed', error);

    return rejectWithValue(error);
  }
});
